import { useEffect, useMemo, useState } from 'react';
import type { BookListItem, BookStatus } from '../../lib/panelApi';
import { deleteBook, listBooks } from '../../lib/panelApi';

const STATUS_META: Record<BookStatus, { label: string; color: string; bg: string }> = {
  draft: { label: 'Draft', color: '#7a5c00', bg: 'rgba(234,179,8,0.16)' },
  published: { label: 'Published', color: '#0a7a3f', bg: 'rgba(16,185,129,0.16)' },
  archived: { label: 'Archived', color: '#6b7280', bg: 'rgba(107,114,128,0.16)' },
};

function StatusBadge({ status }: { status: BookStatus }) {
  const meta = STATUS_META[status] ?? STATUS_META.draft;
  return (
    <span
      className="pill"
      title="Publication status"
      style={{ color: meta.color, background: meta.bg, fontWeight: 850 }}
    >
      {meta.label}
    </span>
  );
}

type Props = {
  accessToken: string;
  onAdd: () => void;
  onEdit: (uuid: string) => void;
};

export function BooksListPage({ accessToken, onAdd, onEdit }: Props) {
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState<BookListItem[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [deletingUuid, setDeletingUuid] = useState<string | null>(null);
  const [q, setQ] = useState('');
  const [freeOnly, setFreeOnly] = useState(false);
  const [statusFilter, setStatusFilter] = useState<'all' | BookStatus>('all');

  async function refresh() {
    setLoading(true);
    setError(null);
    try {
      const res = await listBooks(accessToken, 100, 0);
      setItems(res.items);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to load books';
      setError(msg);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessToken]);

  const stats = useMemo(() => {
    return {
      total: items.length,
      free: items.filter((x) => x.is_free).length,
    };
  }, [items]);

  const filtered = useMemo(() => {
    const query = q.trim().toLowerCase();
    return items.filter((b) => {
      if (freeOnly && !b.is_free) return false;
      if (statusFilter !== 'all' && b.status !== statusFilter) return false;
      if (!query) return true;
      const t = (b.title || '').toLowerCase();
      const a = (b.authors || []).join(', ').toLowerCase();
      return t.includes(query) || a.includes(query);
    });
  }, [items, q, freeOnly, statusFilter]);

  async function onDelete(uuid: string) {
    const ok = confirm('Delete this book? This cannot be undone.');
    if (!ok) return;
    setDeletingUuid(uuid);
    setError(null);
    try {
      await deleteBook(accessToken, uuid);
      setItems((prev) => prev.filter((x) => x.uuid !== uuid));
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Delete failed';
      setError(msg);
    } finally {
      setDeletingUuid(null);
    }
  }

  return (
    <div className="card">
      <div className="pageHeader" style={{ marginBottom: 8 }}>
        <div>
          <div className="pageTitle">Books</div>
          <div className="pageSubtitle">Manage your library: audio files, access, and structured content.</div>
        </div>
        <div className="row">
          <span className="pill" title="Total books">
            <span className="muted">Total</span>
            <span>{stats.total}</span>
          </span>
          <span className="pill" title="Free books">
            <span className="muted">Free</span>
            <span>{stats.free}</span>
          </span>
        </div>
      </div>

      <div className="toolbar" style={{ marginBottom: 10 }}>
        <div className="toolbarGroup">
          <button className="btn btn-primary" onClick={onAdd} disabled={loading}>
            Add book
          </button>
          <button className="btn" onClick={refresh} disabled={loading}>
            {loading ? 'Loading…' : 'Refresh'}
          </button>
        </div>
        <div className="toolbarGroup" style={{ flex: 1, justifyContent: 'flex-end', alignItems: 'flex-end' }}>
          <div className="field" style={{ marginTop: 0, minWidth: 240, flex: 1, maxWidth: 420 }}>
            <div className="label">Search</div>
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Filter by title or author…" />
          </div>
          <div className="field" style={{ marginTop: 0, minWidth: 150 }}>
            <div className="label">Status</div>
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value as 'all' | BookStatus)}>
              <option value="all">All statuses</option>
              <option value="draft">Draft</option>
              <option value="published">Published</option>
              <option value="archived">Archived</option>
            </select>
          </div>
          <label className="checkField" title="Show only free books">
            <input type="checkbox" checked={freeOnly} onChange={(e) => setFreeOnly(e.target.checked)} />
            <span>Free only</span>
          </label>
        </div>
      </div>

      {error ? (
        <div style={{ marginBottom: 10 }}>
          <div className="errorBox">{error}</div>
        </div>
      ) : null}

      {loading ? (
        <div className="muted">Loading books…</div>
      ) : filtered.length === 0 ? (
        <div className="muted">No books yet. Click “Add Book”.</div>
      ) : (
        <div className="list">
          {filtered.map((b) => (
            <div className="item" key={b.uuid}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'baseline' }}>
                <div>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                    <div style={{ fontWeight: 850, fontSize: 14 }}>{b.title || 'Untitled'}</div>
                    <StatusBadge status={b.status} />
                    {b.is_free ? (
                      <span className="pill pill-ok" title="Free book">
                        Free
                      </span>
                    ) : null}
                  </div>
                  <div className="muted" style={{ marginTop: 4, fontSize: 12 }}>
                    {b.authors?.length ? b.authors.join(', ') : 'No authors'} • {new Date(b.created_at).toLocaleString()}
                  </div>
                </div>
                <div className="row" style={{ gap: 8 }}>
                  <button className="btn btn-secondary" onClick={() => onEdit(b.uuid)}>
                    Edit
                  </button>
                  <button className="btn btn-danger" onClick={() => onDelete(b.uuid)} disabled={deletingUuid === b.uuid}>
                    {deletingUuid === b.uuid ? 'Deleting…' : 'Delete'}
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

